import {useCallback, useMemo, useRef, useState} from 'react';
import {Pressable, StyleSheet, View} from 'react-native';
import {Text} from './Components';
import {useTheme} from './Hooks';
import {RootStackParams} from './Typings/NavigationTypes';

import About from './Screens/ADAPT-MH/About';
import Analytics from './Screens/ADAPT-MH/Analytics';
import Customize from './Screens/ADAPT-MH/Customize';
import DataSharing from './Screens/ADAPT-MH/DataSharing';
import Orientation from './Screens/ADAPT-MH/Orientation';
import PrivacyIntro from './Screens/ADAPT-MH/PrivacyIntro';
import ProviderAssessment from './Screens/ADAPT-MH/ProviderAssessment';
import ProviderDashboard from './Screens/ADAPT-MH/ProviderDashboard';
import ProviderLogin from './Screens/ADAPT-MH/ProviderLogin';
import Results from './Screens/ADAPT-MH/Results';
import Support from './Screens/ADAPT-MH/Support';
import Heatmap from './Screens/Heatmap/Heatmap';
import Landing from './Screens/Landing/Landing';
import Questions from './Screens/Questions/Questions';
import RegionalSettings from './Screens/RegionalSettings/RegionalSettings';
import Training from './Screens/Training/Training';

type RouteName = keyof RootStackParams;
type RouteEntry = {
  key: string;
  name: RouteName;
  params: RootStackParams[RouteName];
  options: {
    title?: string;
    headerRight?: () => React.ReactNode;
  };
};

type BeforeRemoveEvent = {
  data: {action: {type: 'GO_BACK'}};
  preventDefault: () => void;
};

type BeforeRemoveListener = (event: BeforeRemoveEvent) => void;

const screens: Record<RouteName, React.ElementType> = {
  Landing,
  PrivacyIntro,
  Orientation,
  Questions,
  Results,
  Support,
  ProviderDashboard,
  ProviderLogin,
  ProviderAssessment,
  About,
  Training,
  Analytics,
  Customize,
  DataSharing,
  RegionalSettings,
  Heatmap,
};

let routeSequence = 0;

const createEntry = <Name extends RouteName>(
  name: Name,
  params: RootStackParams[Name],
): RouteEntry => {
  routeSequence += 1;
  return {
    key: `${name}-${routeSequence}`,
    name,
    params,
    options: {},
  };
};

const Router: React.FC = () => {
  const theme = useTheme();
  const [stack, setStack] = useState<RouteEntry[]>([createEntry('Landing', undefined)]);
  const listeners = useRef(new Map<string, Set<BeforeRemoveListener>>());
  const current = stack[stack.length - 1];

  const rawGoBack = useCallback(() => {
    setStack((currentStack) =>
      currentStack.length > 1 ? currentStack.slice(0, currentStack.length - 1) : currentStack,
    );
  }, []);

  const attemptGoBack = useCallback(() => {
    let prevented = false;
    const event: BeforeRemoveEvent = {
      data: {action: {type: 'GO_BACK'}},
      preventDefault: () => {
        prevented = true;
      },
    };

    listeners.current.get(current.key)?.forEach((listener) => listener(event));
    if (!prevented) {
      rawGoBack();
    }
  }, [current.key, rawGoBack]);

  const navigation = useMemo(
    () => ({
      navigate: <Name extends RouteName>(name: Name, params?: RootStackParams[Name]) => {
        setStack((currentStack) => [...currentStack, createEntry(name, params)]);
      },
      replace: <Name extends RouteName>(name: Name, params?: RootStackParams[Name]) => {
        setStack((currentStack) => [
          ...currentStack.slice(0, currentStack.length - 1),
          createEntry(name, params),
        ]);
      },
      goBack: attemptGoBack,
      setOptions: (options: RouteEntry['options']) => {
        setStack((currentStack) =>
          currentStack.map((entry) =>
            entry.key === current.key ? {...entry, options: {...entry.options, ...options}} : entry,
          ),
        );
      },
      addListener: (eventName: string, listener: BeforeRemoveListener) => {
        if (eventName !== 'beforeRemove') {
          return () => undefined;
        }

        const routeListeners = listeners.current.get(current.key) || new Set();
        routeListeners.add(listener);
        listeners.current.set(current.key, routeListeners);
        return () => routeListeners.delete(listener);
      },
      removeListener: (eventName: string, listener: BeforeRemoveListener) => {
        if (eventName === 'beforeRemove') {
          listeners.current.get(current.key)?.delete(listener);
        }
      },
      dispatch: (action: {type?: string}) => {
        if (action.type === 'GO_BACK') {
          rawGoBack();
        }
      },
    }),
    [attemptGoBack, current.key, rawGoBack],
  );

  const Screen = screens[current.name];
  const showHeader = current.name !== 'Landing';
  const headerRight = current.options.headerRight?.();
  const headerColors = {
    backgroundColor: theme.colors.surface,
    borderBottomColor: theme.colors.border,
  };

  return (
    <View style={styles.root}>
      {showHeader ? (
        <View style={[styles.header, headerColors]}>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Navigate up"
            onPress={attemptGoBack}
            style={styles.backButton}
          >
            <Text fontSize={34} lineHeight={36} style={styles.backIcon}>
              ‹
            </Text>
          </Pressable>
          <Text weight="bold" fontSize={20} style={styles.headerTitle}>
            {current.options.title || ''}
          </Text>
          <View style={styles.headerRight}>{headerRight}</View>
        </View>
      ) : null}

      <View style={styles.screen}>
        <Screen
          navigation={navigation}
          route={{
            key: current.key,
            name: current.name,
            params: current.params,
          }}
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  root: {
    flex: 1,
  },
  header: {
    minHeight: 58,
    borderBottomWidth: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  backButton: {
    width: 58,
    minHeight: 58,
    alignItems: 'center',
    justifyContent: 'center',
  },
  backIcon: {
    marginTop: -4,
  },
  headerTitle: {
    flex: 1,
  },
  headerRight: {
    minWidth: 58,
    alignItems: 'center',
    justifyContent: 'center',
  },
  screen: {
    flex: 1,
  },
});

export default Router;
